/**
 * The build string this client reports to the backend on connect (#836).
 *
 * The server side of #836 has been live and working since it shipped — but only
 * for browsers. Measured on prod 2026-08-13: 3 of 26 `owner` devices reported a
 * version (newest `web@2.1.0+8867b768`, minutes old), and **0 of 14 `agent`
 * devices ever had.** The web client sent the parameter; no agent client did.
 *
 * The cost was not theoretical. Answering one question for #826 — "is this agent
 * running the build with the KeyPackage replenish fix?" — took four local
 * techniques (`openclaw plugins inspect`, `importlib.util.find_spec` inside a
 * venv, `grep` for a symbol the fix introduced, and `ps -o lstart` against
 * `git log -G`). That worked only because five of six agents happened to run on
 * one laptop. The sixth did not, and its status is still unknown. For a
 * customer's agent on a customer's machine, every agent is the sixth.
 *
 * Sent as a `client_version` query parameter on the WebSocket handshake, which
 * `client-version.ts` in packages/web calls "the only capture point available to
 * EVERY client". The connect path is also the correct one: an upgrade means a
 * restart means a reconnect, so this tracks the RUNNING build rather than
 * whatever was installed at enrollment.
 *
 * ⚠️ Self-reported and unauthenticated. It is a diagnostic signal; nothing may
 * branch on it. The server bounds length and charset again before storing
 * (`_sanitize_client_version`, ws.py:372).
 */

const PACKAGE = "@agentvault/agentvault";
const FALLBACK = "0.0.0-dev";

/** Mirrors the server's `_CLIENT_VERSION_MAX_LEN` (ws.py:368). */
const MAX_LEN = 64;
/** Mirrors the server's `_CLIENT_VERSION_ALLOWED` charset (ws.py:369). */
const DISALLOWED = /[^A-Za-z0-9._@/+-]/g;

/** This package's own identity, e.g. `@agentvault/agentvault@0.23.21`. */
function ownIdentity(): string {
  // __AV_VERSION__ is injected by esbuild from package.json at build time
  // (build.mjs:71); the guard keeps unbundled contexts (tests) working.
  const v = typeof __AV_VERSION__ !== "undefined" ? __AV_VERSION__ : FALLBACK;
  return `${PACKAGE}@${v}`;
}

/**
 * Build the reported client version, preferring a host-supplied `override`.
 *
 * The override exists because @agentvault/claude-bridge BUNDLES this package's
 * dist, so without it every bridge agent would report the inner plugin version
 * and the operator would have no way to see the bridge build they actually
 * installed — which is the exact confusion #836 was opened about (a bridge on
 * 0.3.2 read as "same bug, didn't restart"). The server's sanitiser docstring
 * already names `@agentvault/claude-bridge@0.7.12` as an expected shape.
 *
 * An override that cannot survive the server's sanitiser is discarded in favour
 * of this package's own identity: a rejected string stores NULL, and a truthful
 * inner version beats "not reported". Never throws — a diagnostic is not worth
 * breaking the WebSocket over.
 */
export function buildClientVersion(override?: string | null): string {
  try {
    const candidate = (override ?? "").trim();
    if (candidate) {
      const cleaned = candidate.replace(DISALLOWED, "").slice(0, MAX_LEN);
      // `..` is rejected server-side even though the charset admits it.
      if (cleaned && !cleaned.includes("..")) return cleaned;
    }
    return ownIdentity().replace(DISALLOWED, "").slice(0, MAX_LEN);
  } catch {
    return FALLBACK;
  }
}
